import React, { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import PropTypes from 'prop-types'

import { faGlobe } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import { generateUrls } from '@hooks/useBrandTheme'
import useBrandThemes from '@hooks/useBrandThemes'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

import BrandThemeListItem from '../BrandThemeListItem'

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'brands',
      },
      key: 'brand_id',
    },
  ]
}

const PageContent = (props) => {
  const { pageFilters } = props

  const brandThemesPayload = useBrandThemes({
    filters: pageFilters,
    performHttpRequests: true,
    requestOptions: defaultRequestOptions.brandTheme,
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredBrandThemes,
    filteredBrandThemesCount,
    hasFilteredBrandThemes,
    loading,
  } = brandThemesPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters, showCreateOrEditBrandThemeModal, updateFilters,
    },
    defaultFilters,
  } = pageContext

  const { brandThemesIndexUrl } = generateUrls()

  return (
    <>
      <Helmet>
        <title>Brand Themes | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button buttonStyle="primaryCreate" onClick={() => showCreateOrEditBrandThemeModal()} size="medium">
            Create
          </Button>
        )}
        activeTabBarItemKey="brandThemes"
        tabBarItems={[
          {
            href: brandThemesIndexUrl,
            icon: faGlobe,
            key: 'brandThemes',
            title: 'Brand Themes',
          },
        ]}
        title="Brand Themes"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredBrandThemes && <ListBlankState />}

            {!loading && hasFilteredBrandThemes && (
              <DashboardModule contentBoxProps={{ flexDirection: 'column' }} title="Brand Themes">
                <Box flexDirection="column">
                  {filteredBrandThemes.map(brandTheme => (
                    <BrandThemeListItem key={brandTheme.id} brandTheme={brandTheme} />
                  ))}
                </Box>

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredBrandThemesCount}
                  loading={loading}
                />
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

PageContent.propTypes = {
  pageFilters: PropTypes.object.isRequired,
}

export default PageContent
